import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { CheckSquare, Square, Calendar, User, MoreHorizontal, Edit, Trash2, Info } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { format } from "date-fns";
import { TaskInfoPopover } from "../TaskInfoPopover";
import { EditTaskModal } from "../EditTaskModal";
import { useDeleteTask, useUpdateTask } from "@/hooks/tasks/useTasks";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { truncateHtmlContent } from "@/utils/htmlUtils";

interface TasksKanbanColumnProps {
  id: string;
  title: string;
  tasks: any[];
  onDragStart: (e: React.DragEvent, taskId: string, sourceColumnId: string) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent, targetColumnId: string) => void;
  statusConfig?: Record<string, {
    color: string;
    bgColor: string;
    borderColor: string;
    headerBg: string;
    headerText: string;
    dotColor: string;
  }>;
  onTaskUpdated?: () => void;
}

export const TasksKanbanColumn = ({
  id,
  title,
  tasks,
  onDragStart,
  onDragOver,
  onDrop,
  statusConfig,
  onTaskUpdated
}: TasksKanbanColumnProps) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [collapsed, setCollapsed] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [deletingTask, setDeletingTask] = useState<any>(null);
  const [updatingTask, setUpdatingTask] = useState<string | null>(null);
  const deleteTaskMutation = useDeleteTask();
  const updateTaskMutation = useUpdateTask();
  
  // Get status-specific colors
  const statusColors = statusConfig?.[title] || {
    color: 'text-gray-600',
    bgColor: 'bg-gray-50',
    borderColor: 'border-gray-200',
    headerBg: 'bg-gray-100',
    headerText: 'text-gray-800',
    dotColor: 'bg-gray-500'
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
    onDragOver(e);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    setIsDragOver(false);
    onDrop(e, id);
  };

  const getPriorityBadge = (priority: string) => {
    const priorityConfig = {
      low: { variant: "secondary" as const, label: t("tasks.priority.low", "Low") },
      medium: { variant: "default" as const, label: t("tasks.priority.medium", "Medium") },
      high: { variant: "warning" as const, label: t("tasks.priority.high", "High") },
      urgent: { variant: "destructive" as const, label: t("tasks.priority.urgent", "Urgent") }
    };
    
    const config = priorityConfig[priority as keyof typeof priorityConfig] || priorityConfig.medium;
    return <Badge variant={config.variant} className="text-xs">{config.label}</Badge>;
  };

  const handleToggleStatus = async (task: any) => {
    if (updatingTask === task.id) return;
    
    setUpdatingTask(task.id);
    try {
      const newStatus = task.status === 'done' ? 'todo' : 'done';
      
      await updateTaskMutation.mutateAsync({
        id: task.id,
        status: newStatus
      });

      if (onTaskUpdated) {
        onTaskUpdated();
      }
    } catch (error) {
      console.error('Error updating task status:', error);
    } finally {
      setUpdatingTask(null);
    }
  };

  const handleEdit = (task: any) => {
    // Just update URL - let parent component handle the modal
    const currentParams = Object.fromEntries(searchParams.entries());
    setSearchParams({ ...currentParams, task: task.id }, { replace: false });
  };

  const handleDelete = (task: any) => {
    setDeletingTask(task);
  };

  const confirmDelete = async () => {
    if (deletingTask) {
      try {
        await deleteTaskMutation.mutateAsync(deletingTask.id);
        setDeletingTask(null);
        if (onTaskUpdated) {
          onTaskUpdated();
        }
      } catch (error) {
        console.error("Failed to delete task:", error);
      }
    }
  };

  // No longer needed - parent handles modal

  // Helper to get days ago string
  function getDaysAgo(dateString: string | undefined): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'today';
    if (diffDays === 1) return '1 day ago';
    return `${diffDays} days ago`;
  }

  return (
    <>
      <div 
        key={id}
        className={`flex flex-col w-80 bg-white/95 dark:bg-black/80 backdrop-blur-sm rounded-sm border border-gray-200 dark:border-gray-700 shadow-sm ${
          isDragOver ? 'ring-2 ring-blue-500 border-blue-500' : ''
        }`}
        style={{
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
      {/* Column Header */}
      <div className={`p-4 border-b border-gray-200 dark:border-gray-700 ${statusColors.headerBg} ${statusColors.headerText} rounded-t-xl`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded-full ${statusColors.dotColor}`}></div>
            <h3 className="font-semibold text-sm">{title}</h3>
            <span className="text-xs bg-white/20 dark:bg-black/20 px-2 py-1 rounded-sm">
              {tasks.length}
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setCollapsed(!collapsed)}
            className="h-6 w-6 p-0"
          >
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Column Content */}
      {!collapsed ? (
        <div className={`flex-1 overflow-y-auto p-3 space-y-3 min-h-[200px] ${
          isDragOver ? 'bg-blue-50/20 dark:bg-blue-900/20' : ''
        }`}>
          {tasks.map(task => {
            const isOverdue = task.due_date && new Date(task.due_date) < new Date() && task.status !== 'done';
            const daysAgo = getDaysAgo(task.created_at);
            
            return (
              <div 
                key={task.id}
                className={`bg-white/95 dark:bg-black/80 backdrop-blur-sm rounded-sm border border-gray-200 dark:border-gray-700 shadow-sm p-3 cursor-move hover:shadow-lg transition-all duration-300 hover:scale-[1.02] group w-full ${
                  isOverdue ? 'border-red-200 bg-red-50/50' : ''
                }`}
                style={{
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'
                }}
                draggable
                onDragStart={(e) => onDragStart(e, task.id, id)}
              >
                <div className="flex justify-between items-start w-full">
                  <div className="flex-1 min-w-0">
                    {/* Task Header */}
                    <div className="flex items-start gap-2 mb-2">
                      <button
                        onClick={() => handleToggleStatus(task)}
                        disabled={updatingTask === task.id}
                        className={`p-1 rounded transition-colors flex-shrink-0 ${
                          task.status === 'done'
                            ? 'text-green-600 hover:text-green-700'
                            : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
                        } ${updatingTask === task.id ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                        title={task.status === 'done' ? t("tasks.actions.markUndone", "Mark as undone") : t("tasks.actions.markDone", "Mark as done")}
                      >
                        {task.status === 'done' ? (
                          <CheckSquare className="h-4 w-4" />
                        ) : (
                          <Square className="h-4 w-4" />
                        )}
                      </button>
                      <h4 className={`font-medium text-sm line-clamp-2 ${(task.status === 'done' || task.status === 'cancelled') ? 'line-through text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-gray-100'}`}>
                        {task.name}
                      </h4>
                    </div>

                    {/* Task Description */}
                    {task.description && (
                      <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2 mb-2">
                        {truncateHtmlContent(task.description, 80)}
                      </p>
                    )}

                    {/* Priority Badge */}
                    <div className="mb-2">
                      {getPriorityBadge(task.priority)}
                    </div>

                    {/* Task Details */}
                    <div className="space-y-1 text-xs text-gray-500 dark:text-gray-400">
                      {task.due_date && (
                        <div className={`flex items-center gap-1 ${isOverdue ? 'text-red-600 font-medium' : ''}`}>
                          <Calendar className="h-3 w-3" />
                          <span>{format(new Date(task.due_date), "MMM dd")}</span>
                          {isOverdue && <span>(Overdue)</span>}
                        </div>
                      )}
                      
                      {task.assignee && (
                        <div className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          <span>{task.assignee.first_name} {task.assignee.last_name}</span>
                        </div>
                      )}
                      
                      <div className="text-gray-400 dark:text-gray-500">
                        {daysAgo}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="h-6 w-6 p-0"
                          title={t("tasks.info", "Task Information")}
                        >
                          <Info className="h-3 w-3" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-80 p-0 max-w-[20rem]" align="end">
                        <TaskInfoPopover task={task} onTaskUpdated={onTaskUpdated} />
                      </PopoverContent>
                    </Popover>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className="h-6 w-6 p-0"
                      onClick={() => handleEdit(task)}
                      title={t("common.edit", "Edit")}
                    >
                      <Edit className="h-3 w-3" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className="h-6 w-6 p-0 text-red-600 hover:text-red-700"
                      onClick={() => handleDelete(task)}
                      title={t("common.delete", "Delete")}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="p-4 text-center text-gray-500 dark:text-gray-400 text-sm">
          {tasks.length} {tasks.length === 1 ? 'task' : 'tasks'}
        </div>
      )}
    </div>

    {/* Delete Confirmation Dialog */}
    <AlertDialog open={!!deletingTask} onOpenChange={(open) => !open && setDeletingTask(null)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t("common.deleteConfirmation", { count: 1 })}</AlertDialogTitle>
          <AlertDialogDescription>
            {t("common.deleteWarning")}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
          <AlertDialogAction
            onClick={confirmDelete}
            className="bg-red-600 hover:bg-red-700"
            disabled={deleteTaskMutation.isPending}
          >
            {deleteTaskMutation.isPending ? t("common.deleting", "Deleting...") : t("common.delete")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
};
